import { useTheme } from "next-themes";
import { useEffect, useMemo, useState } from "react";
import { VegaEmbed } from "react-vega";
import type { ChartData } from "@/lib/utils/vega-chart-shared";
import { createPieChartSpec } from "./vega-specs";

type PieChartProps = {
  data: ChartData[];
  width?: number;
  height?: number;
  innerRadius?: number;
  title?: string;
  chartColors?: string[] | null;
  lang?: string;
};

/**
 * Client-side pie chart component using Vega-Lite
 *
 * Uses the same chart specification as server-side rendering,
 * ensuring consistent appearance in UI and PDFs.
 */
export function PieChart({
  data,
  width = 500,
  height = 400,
  innerRadius = 0.5,
  title,
  chartColors,
  lang,
}: PieChartProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === "dark";

  // Generate spec on client side and ensure it's serializable
  const spec = useMemo(() => {
    const chartSpec = createPieChartSpec(data, {
      width,
      height,
      innerRadius,
      title,
      isDark,
      chartColors: chartColors || undefined,
      lang,
    });
    // Ensure the spec is JSON-serializable by parsing/stringifying
    return JSON.parse(JSON.stringify(chartSpec));
  }, [data, width, height, innerRadius, title, isDark, chartColors, lang]);

  return (
    <div className="w-full flex justify-center">
      <VegaEmbed spec={spec} options={{ actions: false }} />
    </div>
  );
}
